import type { ReactElement } from 'react';
import React, { useMemo } from 'react';
import { ascendingOrder } from 'helpers/tanstack-query.helpers';
import { useAreas } from 'services/assist-api/entities/area/areas';
import { useCustomerAreas } from 'services/assist-api/entities/customers-areas/customers-areas';
import { ROLE } from 'services/assist-api/entities/role/roles';
import { useTestInstanceFilterAreas } from 'services/assist-api/entities/test-instance/filters/area';
import { useAuthCustomer, useRoleMatch } from 'services/providers/auth.hooks';
import type { DashboardArea } from './teacher-dashboard';
import TeacherDashboard from './teacher-dashboard';

export default function MySubscriptionsTab(): ReactElement {
  const canViewSurveys = useRoleMatch([ROLE.ADMIN, ROLE.PRODUCT_ADMIN], true);
  const customer = useAuthCustomer();
  const customerAreas = useCustomerAreas({ filters: { customer: customer?.id } });
  const areas = useAreas({ order: ascendingOrder });
  const historicAreas = useTestInstanceFilterAreas(
    { filters: { customer: customer?.id } },
    { enabled: !!customer?.id }
  );

  const filteredAreas = useMemo(
    () =>
      areas.data?.json.reduce<DashboardArea[]>((array, area) => {
        const customerArea = customerAreas.data?.json.find(
          (custArea) => custArea.area.id === area.id
        );
        const isActive = !!customerArea?.enabled;
        const isHistoric = !!historicAreas.data?.json.find((a) => a.id === area.id);

        const dashboardArea = { ...area, customerArea, isActive, isHistoric };

        // surveys are only showed if the organisation is subscribed & the user can view them
        if (area.type === 'survey') {
          return isActive && canViewSurveys ? [...array, dashboardArea] : array;
        }

        // other areas just require the organisation to be subscribed or have historic data
        return isActive || isHistoric ? [...array, dashboardArea] : array;
      }, []),
    [areas.data?.json, canViewSurveys, customerAreas.data?.json, historicAreas.data?.json]
  );

  return <TeacherDashboard areas={filteredAreas} />;
}
